import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useCareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { careplanQueryOptions } from "@/domains/careplan/factories/careplan-query-options";
import { createCareplanOrderModel } from "@/domains/careplan/factories/create-careplan-order-model";
import { getCareplanOrderStatus } from "@/domains/careplan/factories/get-careplan-order-status";
import type { CareplanProductDataModel } from "@/domains/careplan/models/types";
import { useCartSellingPlans } from "@/domains/careplan/queries/use-cart-selling-plans";
import { useCartStore } from "@/stores";

export const useCareplanOrder = () => {
  const { cartVariantIds, productsWithHistory, subscription } = useCareplanContext();
  const { data: cart, isError: isCartError } = useQuery({ ...careplanQueryOptions.cart });
  const { isError: isSellingPlansError } = useCartSellingPlans(cartVariantIds);
  const cartCreationFailed = useCartStore((state) => {
    return state.cartCreationFailed;
  });
  const recommendedCartLines = useCartStore((state) => {
    return state.recommendedCartLines;
  });

  const products = useMemo(() => {
    return productsWithHistory.filter((product): product is CareplanProductDataModel => {
      return !product.isPrevious;
    });
  }, [productsWithHistory]);

  const { isError, isLoading } = getCareplanOrderStatus({
    hasCart: !!cart,
    isCartError,
    isSellingPlansError,
    cartCreationFailed,
  });

  const order = useMemo(() => {
    return cart && !isError
      ? createCareplanOrderModel({ cart, products, recommendedCartLines, subscription })
      : null;
  }, [cart, isError, products, recommendedCartLines, subscription]);

  return { order, isLoading, isError };
};

export const useCareplanOrderLine = (variantId: string | null) => {
  const { isError, isLoading, order } = useCareplanOrder();

  const line =
    variantId && order
      ? (order.lines.find((orderLine) => {
          return orderLine.variantId === variantId;
        }) ?? null)
      : null;

  return { isError, isLoading, line, order };
};
